import { useCallback } from 'react';
import { useNavigate } from 'react-router-dom';

import { ModalsProvider } from '@speeki/global-ui-components';

import { MfaMetadata } from '@@types/CommonTypes';

import { useGetServiceRedirect } from '@utils/hooks/useServiceRedirect';
import { useUserSession } from '@utils/hooks/useUserSession';

import { AuthService } from '@services/AuthService/AuthService';
import { getPath } from '@services/RouteService/paths';
import { sentry } from '@services/Sentry';

import {
    DefaultContext,
    FetchResult,
    MutationFunctionOptions,
} from '@apollo/client';
import {
    sessionProcessingVar,
    signUpFormDataVar,
} from '@boot/reactiveVariables';

interface SessionOptions {
    tokens: {
        metadata?: MfaMetadata | null;
        refreshToken?: string | null;
        sessionToken: string;
    };
}

export const useInitializeSession = <
    Data extends SessionOptions,
    Variables,
    Context extends DefaultContext,
>(
    loginAction: (
        args: MutationFunctionOptions<Data, Variables, Context>,
    ) => Promise<FetchResult<Data>>,
    redirectToService: boolean,
) => {
    const [getSessionUser] = useUserSession({
        context: { logoutOnError: false },
    });
    const navigate = useNavigate();
    const serviceRedirect = useGetServiceRedirect();

    const initializeSession = useCallback(
        async (
            variables: MutationFunctionOptions<Data, Variables>['variables'],
            callbacks?: {
                onError?: (e: unknown) => void;
                onSuccess?: (data?: Data | null) => void;
            },
        ) => {
            try {
                sessionProcessingVar(true);

                const { data, errors } = await loginAction({
                    variables,
                });
                if (!data || !!errors?.length) {
                    throw new Error('loginAction error');
                }

                const {
                    tokens: { metadata, refreshToken, sessionToken },
                } = data;

                AuthService.setSessionToken(sessionToken);
                void sentry.setUser();
                refreshToken && AuthService.setRefreshToken(refreshToken);

                if (!metadata) {
                    const { error: baseUserError } = await getSessionUser();
                    if (baseUserError) {
                        throw new Error('getBaseUser error');
                    }

                    callbacks?.onSuccess?.(data);
                    redirectToService &&
                        navigate(
                            getPath({
                                params: serviceRedirect.params,
                                path: serviceRedirect.path,
                            }),
                        );
                } else {
                    const { requireSetup } = metadata;

                    const handleMfaSetup = () => {
                        ModalsProvider.close('TermsAndConditionsInfoModal');
                        signUpFormDataVar(null);
                    };

                    AuthService.setMfaMetadata(metadata);

                    requireSetup && handleMfaSetup();
                    navigate(
                        requireSetup
                            ? getPath({ path: '/auth/setup-mfa' })
                            : getPath({ path: '/auth/enter-code' }),
                    );
                }
            } catch (e) {
                callbacks?.onError?.(e);
            } finally {
                sessionProcessingVar(false);
            }
        },
        [],
    );

    return { initializeSession };
};
